import { useState } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Icon } from '../../display/Icon';
import { FormLabel } from '../../forms/FormLabel';
import { Colors, Typography } from '../../../theme';
import { useOverflow } from '../../../hooks/useOverflow';
import { uniqueId } from '../../../utils/uniqueId';
const Styles = labelStyles('Field', {
    root: css `
    display: grid;
    grid-gap: 5px;
  `,
    helpTextWrapper: css `
    ${Typography.smallPrint1}
  `,
    helpText: css `
    display: grid;
    grid-template-columns: 1fr auto;
    grid-gap: 10px;
    align-items: start;
    color: ${Colors.neutral700};
  `,
    helpTextInner: css `
    padding-top: 1px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    // Without this truncation won't properly occur.
    * {
      display: inline;
      margin: 0;
    }
  `,
    helpTextToggle: css `
    cursor: pointer;
    text-decoration: underline;
    text-transform: lowercase;
    // Prevent help text toggle size from changing.
    width: 33px;
    &:hover,
    &:focus {
      outline: 1px solid ${Colors.blue};
      outline-offset: 1px;
      color: ${Colors.blue};
    }
  `,
    error: css `
    display: inline;
    color: ${Colors.error400};
  `,
});
const HelpText = ({ children, helpTextLineClamp }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isOverflowing, setOverflowingNode] = useOverflow();
    const toggle = isOverflowing && (isolateComponent("button", { "aria-hidden": "true", css: Styles.helpTextToggle, onClick: () => setIsOpen(!isOpen), type: "button" }, isOpen ? 'Less' : 'More'));
    const style = {
        WebkitLineClamp: isOpen ? undefined : helpTextLineClamp,
    };
    return (isolateComponent("div", { css: Styles.helpText },
        isolateComponent("div", { css: Styles.helpTextInner, ref: (node) => setOverflowingNode(node), style: style },
            isolateComponent("div", null, children)),
        toggle));
};
const defaultProps = {
    helpTextLineClamp: 1,
};
export const Field = (_props) => {
    const props = Object.assign(Object.assign({}, defaultProps), _props);
    const [id] = useState(props.inputId || uniqueId('Field-'));
    const describedById = `${id}-description`;
    const helpText = props.renderHelpText && props.renderHelpText();
    return (isolateComponent("div", { css: Styles.root },
        isolateComponent("div", null, props.renderLabel ? (props.renderLabel({
            htmlFor: id,
            isDisabled: props.isDisabled,
            isErrored: !!props.error,
            isRequired: props.isRequired,
            label: props.label,
            requiredText: props.requiredText,
        })) : (isolateComponent(FormLabel, { alignItems: "start", htmlFor: id, isDisabled: props.isDisabled, isErrored: !!props.error, isRequired: props.isRequired, requiredText: props.requiredText, size: "small" },
            props.error && (isolateComponent(Icon, { color: "error400", isBlock: true, name: "formXCircle", size: 18 })),
            isolateComponent("span", null, props.label)))),
        isolateComponent("div", null, props.renderInput({
            ariaDescribedBy: helpText || props.error ? describedById : undefined,
            id,
            isDisabled: !!props.isDisabled,
            isErrored: !!props.error,
            isRequired: !!props.isRequired,
        })),
        isolateComponent("div", { "aria-live": props.error ? 'polite' : 'off', css: Styles.helpTextWrapper, id: describedById }, (helpText || props.error) && (isolateComponent(HelpText, { helpTextLineClamp: props.helpTextLineClamp },
            props.error && isolateComponent("div", { css: Styles.error },
                props.error,
                " "),
            helpText)))));
};
//# sourceMappingURL=Field.js.map
// @zapier/design-system@5.32.5 (2004a9d50)
